import type { FormEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  Bell,
  Bot,
  ChevronDown,
  Heart,
  Home,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  Plus,
  QrCode,
  Send,
  Settings as SettingsIcon,
  ShieldCheck,
  Sparkles,
  Ticket,
  User as UserIcon,
  X,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/store/auth";
import { aiApi, authApi, bookingApi, destinationApi, hotelApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import type { Booking, Destination, Hotel, User } from "@/types";
import { useWishlist } from "@/hooks/useWishlist";
import { asArray } from "@/lib/asArray";

type Tab = "overview" | "bookings" | "passes" | "wishlist" | "profile" | "settings";

const NAV: { id: Tab; label: string; icon: LucideIcon }[] = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "bookings", label: "My Bookings", icon: Ticket },
  { id: "passes", label: "My Digital Passes", icon: QrCode },
  { id: "wishlist", label: "Wishlist", icon: Heart },
  { id: "profile", label: "Profile & KYC", icon: ShieldCheck },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Dashboard() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const [tab, setTabState] = useState<Tab>(
    tabParam && NAV.some((n) => n.id === tabParam) ? (tabParam as Tab) : "overview",
  );

  // Deep-link support: keep the active tab in sync with ?tab= (e.g. from the
  // navbar profile menu) even when the dashboard is already mounted.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && NAV.some((n) => n.id === t) && t !== tab) setTabState(t as Tab);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTab = (t: Tab) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);

  const refreshBookings = () =>
    bookingApi.list().then((r) => setBookings(asArray<Booking>(r.data)));

  useEffect(() => {
    refreshBookings().catch(() => {});
    destinationApi
      .list(true)
      .then((r) => setDestinations(asArray<Destination>(r.data)))
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Book a destination pass, then confirm via mock UPI -> issues a QR pass.
  // Returns whether it succeeded so the caller only closes the modal on success.
  const bookDestination = async (d: Destination): Promise<boolean> => {
    setBooking(true);
    setBookError(null);
    try {
      const created = await bookingApi.create({
        destination_public_id: d.public_id,
        quantity: 1,
      });
      await bookingApi.confirm(created.data.public_id);
      await refreshBookings();
      return true;
    } catch (err) {
      setBookError(errorMessage(err, "Could not complete the booking"));
      return false;
    } finally {
      setBooking(false);
    }
  };

  if (!user) return null;

  const passes = bookings.filter((b) => b.qr_payload && b.status === "confirmed");
  const upcoming = bookings.filter((b) => b.status === "pending" || b.status === "confirmed");
  const title = NAV.find((n) => n.id === tab)?.label ?? "Overview";

  return (
    <div className="flex min-h-screen bg-cream">
      <Sidebar
        tab={tab}
        setTab={(t) => {
          setTab(t);
          setSidebarOpen(false);
        }}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopHeader
          title={title}
          user={user}
          onMenu={() => setSidebarOpen(true)}
          onLogout={handleLogout}
          goProfile={() => setTab("profile")}
        />

        <main className="flex-1 p-5 sm:p-8">
          {tab === "overview" && (
            <OverviewTab
              user={user}
              activePasses={passes.length}
              upcoming={upcoming.length}
              onGoProfile={() => setTab("profile")}
              onGoBookings={() => setTab("bookings")}
            />
          )}
          {tab === "bookings" && (
            <BookingsTab
              bookings={bookings}
              destinations={destinations}
              booking={booking}
              bookError={bookError}
              onBook={bookDestination}
            />
          )}
          {tab === "passes" && <PassesTab passes={passes} onGoBookings={() => setTab("bookings")} />}
          {tab === "wishlist" && <WishlistTab />}
          {tab === "profile" && (
            <ProfileKycTab user={user} onVerified={(u) => setUser(u)} />
          )}
          {tab === "settings" && <SettingsTab onLogout={handleLogout} />}
        </main>
      </div>

      <AIWidget />
    </div>
  );
}

/* ------------------------------- Sidebar -------------------------------- */

function Sidebar({
  tab,
  setTab,
  open,
  onClose,
}: {
  tab: Tab;
  setTab: (t: Tab) => void;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={onClose} />}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-navy-900 text-white transition-transform lg:static lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 items-center justify-between px-5">
          <Logo variant="light" withTagline={false} />
          <button onClick={onClose} className="text-white/70 lg:hidden" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-4">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = tab === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setTab(n.id)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                  active
                    ? "bg-iris-500 text-white"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon size={18} /> {n.label}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-3">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gold-400 transition hover:bg-white/5"
          >
            <Home size={18} /> Go to Website
          </Link>
        </div>
      </aside>
    </>
  );
}

/* ------------------------------ Top header ------------------------------ */

function TopHeader({
  title,
  user,
  onMenu,
  onLogout,
  goProfile,
}: {
  title: string;
  user: User;
  onMenu: () => void;
  onLogout: () => void;
  goProfile: () => void;
}) {
  const [menu, setMenu] = useState<"profile" | "notif" | null>(null);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-5 sm:px-8">
      <div className="flex items-center gap-3">
        <button onClick={onMenu} className="text-navy-800 lg:hidden" aria-label="Open menu">
          <Menu size={22} />
        </button>
        <h1 className="font-display text-lg font-semibold text-navy-900">{title}</h1>
      </div>

      {menu && <div className="fixed inset-0 z-30" onClick={() => setMenu(null)} />}

      <div className="relative z-40 flex items-center gap-2 sm:gap-3">
        <Link
          to="/"
          className="hidden items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-navy-800 transition hover:bg-slate-50 sm:flex"
        >
          <Home size={14} /> Go to Website
        </Link>

        <div className="relative">
          <button
            onClick={() => setMenu(menu === "notif" ? null : "notif")}
            className="relative grid h-9 w-9 place-items-center rounded-full text-navy-700 transition hover:bg-slate-100"
            aria-label="Notifications"
          >
            <Bell size={18} />
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-iris-500" />
          </button>
          {menu === "notif" && (
            <div className="absolute right-0 top-full mt-3 w-64 rounded-xl border border-slate-100 bg-white p-4 text-sm text-slate-500 shadow-lift">
              <p className="font-medium text-navy-900">Notifications</p>
              <p className="mt-2 text-xs">You&apos;re all caught up — no new notifications.</p>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setMenu(menu === "profile" ? null : "profile")}
            className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition hover:bg-slate-100"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-iris-500 text-white">
              <UserIcon size={16} />
            </span>
            <span className="hidden max-w-[110px] truncate text-sm font-medium text-navy-900 sm:block">
              {user.full_name.split(" ")[0]}
            </span>
            <ChevronDown size={14} className={`text-navy-600 transition-transform ${menu === "profile" ? "rotate-180" : ""}`} />
          </button>
          {menu === "profile" && (
            <div className="absolute right-0 top-full mt-3 w-52 overflow-hidden rounded-xl border border-slate-100 bg-white py-1 shadow-lift">
              <div className="border-b border-slate-100 px-4 py-2.5">
                <div className="truncate text-sm font-semibold text-navy-900">{user.full_name}</div>
                <div className="truncate text-xs text-slate-500">
                  {user.email ?? user.phone ?? "Signed in"}
                </div>
              </div>
              <Link to="/" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-navy-800 transition hover:bg-cream">
                <Home size={16} className="text-iris-500" /> Go to Website
              </Link>
              <button
                onClick={() => {
                  setMenu(null);
                  goProfile();
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-navy-800 transition hover:bg-cream"
              >
                <ShieldCheck size={16} className="text-iris-500" /> Profile & KYC
              </button>
              <button
                onClick={onLogout}
                className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-sm text-red-600 transition hover:bg-red-50"
              >
                <LogOut size={16} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/* ------------------------------- Overview ------------------------------- */

function OverviewTab({
  user,
  activePasses,
  upcoming,
  onGoProfile,
  onGoBookings,
}: {
  user: User;
  activePasses: number;
  upcoming: number;
  onGoProfile: () => void;
  onGoBookings: () => void;
}) {
  const verified = user.kyc_status === "verified";
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-navy-900">
          Namaste, {user.full_name.split(" ")[0]} 👋
        </h2>
        <p className="mt-1 text-sm text-slate-500">Here&apos;s a snapshot of your journey.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={QrCode} label="Active Passes" value={activePasses} />
        <StatCard icon={Ticket} label="Upcoming Trips" value={upcoming} />
        <StatCard
          icon={verified ? BadgeCheck : ShieldCheck}
          label="KYC Status"
          value={verified ? "Verified" : "Pending"}
          tone={verified ? "green" : "amber"}
        />
      </div>

      {!verified && (
        <button
          onClick={onGoProfile}
          className="flex w-full items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-left transition hover:bg-amber-100"
        >
          <span className="flex items-center gap-3">
            <AlertCircle size={20} className="shrink-0 text-amber-500" />
            <span>
              <span className="block text-sm font-semibold text-navy-900">
                Your profile is incomplete
              </span>
              <span className="block text-xs text-slate-600">
                Complete Aadhaar KYC to book verified stays and passes.
              </span>
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-amber-700">
            Complete now <ArrowRight size={15} />
          </span>
        </button>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <QuickLink
          title="Browse & book"
          body="Explore heritage destinations and stays on the main site."
          to="/#destinations"
          external
        />
        <button
          onClick={onGoBookings}
          className="rounded-xl border border-slate-200 bg-white p-5 text-left shadow-card transition hover:shadow-lift"
        >
          <h3 className="font-display text-base font-semibold text-navy-900">My bookings</h3>
          <p className="mt-1 text-sm text-slate-500">
            View your trips and digital passes in one place.
          </p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-iris-600">
            Open <ArrowRight size={15} />
          </span>
        </button>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone = "iris",
}: {
  icon: LucideIcon;
  label: string;
  value: string | number;
  tone?: "iris" | "green" | "amber";
}) {
  const toneClass =
    tone === "green"
      ? "bg-green-50 text-green-600"
      : tone === "amber"
        ? "bg-amber-50 text-amber-600"
        : "bg-iris-50 text-iris-500";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <span className={`grid h-11 w-11 place-items-center rounded-xl ${toneClass}`}>
        <Icon size={22} strokeWidth={1.8} />
      </span>
      <div className="mt-3 font-display text-2xl font-bold text-navy-900">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

function QuickLink({
  title,
  body,
  to,
  external,
}: {
  title: string;
  body: string;
  to: string;
  external?: boolean;
}) {
  const inner = (
    <>
      <h3 className="font-display text-base font-semibold text-navy-900">{title}</h3>
      <p className="mt-1 text-sm text-slate-500">{body}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-iris-600">
        Explore <ArrowRight size={15} />
      </span>
    </>
  );
  const cls =
    "block rounded-xl border border-slate-200 bg-white p-5 shadow-card transition hover:shadow-lift";
  return external ? (
    <a href={to} className={cls}>
      {inner}
    </a>
  ) : (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  );
}

/* ------------------------------ My Bookings ----------------------------- */

function BookingsTab({
  bookings,
  destinations,
  booking,
  bookError,
  onBook,
}: {
  bookings: Booking[];
  destinations: Destination[];
  booking: boolean;
  bookError: string | null;
  onBook: (d: Destination) => Promise<boolean>;
}) {
  const [modal, setModal] = useState(false);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-navy-900">My Bookings</h2>
          <p className="text-sm text-slate-500">Everything you&apos;ve booked, in one place.</p>
        </div>
        <button onClick={() => setModal(true)} className="btn-primary">
          <Plus size={16} /> Book a Pass
        </button>
      </div>

      {bookings.length === 0 ? (
        <EmptyState
          icon={Ticket}
          title="No bookings yet"
          body="Book a heritage pass to get started — it'll appear here."
          action={
            <button onClick={() => setModal(true)} className="btn-primary mt-4">
              <Plus size={16} /> Book a Pass
            </button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
          <ul className="divide-y divide-slate-100">
            {bookings.map((b) => (
              <li key={b.public_id} className="flex items-center justify-between gap-3 p-4">
                <div>
                  <div className="font-mono text-sm text-navy-900">{b.qr_payload ?? "Pending payment"}</div>
                  <div className="text-xs text-slate-500">
                    ₹{b.amount.toFixed(0)} · {b.quantity} ticket(s) ·{" "}
                    {new Date(b.created_at).toLocaleDateString("en-IN")}
                  </div>
                </div>
                <StatusBadge status={b.status} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {modal && (
        <BookingModal
          destinations={destinations}
          busy={booking}
          error={bookError}
          onClose={() => setModal(false)}
          onBook={async (d) => {
            const ok = await onBook(d);
            if (ok) setModal(false);
          }}
        />
      )}
    </div>
  );
}

function BookingModal({
  destinations,
  busy,
  error,
  onClose,
  onBook,
}: {
  destinations: Destination[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onBook: (d: Destination) => Promise<void>;
}) {
  const [selected, setSelected] = useState<string>(destinations[0]?.public_id ?? "");
  const chosen = destinations.find((d) => d.public_id === selected);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lift"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-navy-900">Book a Heritage Pass</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-navy-700" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
            {error}
          </div>
        )}

        {destinations.length === 0 ? (
          <p className="text-sm text-slate-500">No destinations available right now.</p>
        ) : (
          <>
            <label className="label">Destination</label>
            <select
              className="input"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              {destinations.map((d) => (
                <option key={d.public_id} value={d.public_id}>
                  {d.name} — ₹{d.ticket_price.toFixed(0)}
                </option>
              ))}
            </select>

            <div className="mt-4 flex items-center justify-between rounded-lg bg-cream px-4 py-3 text-sm">
              <span className="text-slate-600">Total (1 ticket, mock UPI)</span>
              <span className="font-semibold text-navy-900">
                ₹{chosen ? chosen.ticket_price.toFixed(0) : "0"}
              </span>
            </div>

            <button
              disabled={busy || !chosen}
              onClick={() => chosen && onBook(chosen)}
              className="btn-primary mt-5 w-full py-3"
            >
              {busy ? "Processing…" : "Confirm & Pay"}
            </button>
            <p className="mt-2 text-center text-xs text-slate-400">
              Payments are mocked in this demo — no real charge.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------- My Digital Passes -------------------------- */

function PassesTab({ passes, onGoBookings }: { passes: Booking[]; onGoBookings: () => void }) {
  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-5">
        <h2 className="font-display text-xl font-bold text-navy-900">My Digital Passes</h2>
        <p className="text-sm text-slate-500">Show these QR passes at monument entry.</p>
      </div>

      {passes.length === 0 ? (
        <EmptyState
          icon={QrCode}
          title="No active passes"
          body="Confirmed bookings generate a QR pass that appears here."
          action={
            <button onClick={onGoBookings} className="btn-primary mt-4">
              Go to My Bookings
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {passes.map((p) => (
            <div key={p.public_id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
              <div className="mb-3 grid h-28 place-items-center rounded-xl bg-navy-900 text-white/90">
                <QrCode size={64} strokeWidth={1.2} />
              </div>
              <div className="font-mono text-sm text-navy-900">{p.qr_payload}</div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-slate-500">₹{p.amount.toFixed(0)}</span>
                <StatusBadge status={p.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Wishlist ------------------------------- */

function WishlistTab() {
  const wishlist = useWishlist();
  const [hotels, setHotels] = useState<Hotel[] | null>(null);

  useEffect(() => {
    hotelApi
      .list()
      .then((r) => setHotels(asArray<Hotel>(r.data)))
      .catch(() => setHotels([]));
  }, []);

  const saved = (hotels ?? []).filter((h) => wishlist.ids.has(h.public_id));

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-5">
        <h2 className="font-display text-xl font-bold text-navy-900">Saved Properties</h2>
        <p className="text-sm text-slate-500">Hotels you&apos;ve saved to your wishlist.</p>
      </div>

      {hotels === null ? (
        <p className="py-10 text-center text-slate-400">Loading…</p>
      ) : saved.length === 0 ? (
        <EmptyState
          icon={Heart}
          title="No saved properties yet"
          body="Browse hotels and tap the heart to save your favourites."
          action={
            <Link to="/hotels" className="btn-primary mt-4">
              Browse hotels
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {saved.map((h) => {
            const img = h.images[0] ?? h.image_url ?? "";
            return (
              <article key={h.public_id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
                <div className="relative h-36 w-full bg-slate-100">
                  {img ? <img src={img} alt={h.name} className="h-full w-full object-cover" /> : null}
                  <button
                    onClick={() => wishlist.remove(h.public_id)}
                    aria-label="Remove from wishlist"
                    className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-white/90 shadow transition hover:bg-white"
                  >
                    <Heart size={15} className="fill-red-500 text-red-500" />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="font-display text-base font-semibold text-navy-900">{h.name}</h3>
                  <p className="flex items-center gap-1 text-xs text-slate-500">
                    <MapPin size={12} className="text-iris-500" /> {h.location}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-display text-lg font-bold text-navy-900">
                      ₹{h.price_per_night.toLocaleString("en-IN")}
                      <span className="text-xs font-normal text-slate-500"> /night</span>
                    </span>
                    <Link to="/hotels" className="btn-primary px-3 py-1.5 text-xs">View</Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- Profile & KYC ---------------------------- */

function ProfileKycTab({ user, onVerified }: { user: User; onVerified: (u: User) => void }) {
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-card">
        <h2 className="mb-4 font-display text-lg font-semibold text-navy-900">Profile</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Full name" value={user.full_name} />
          <Field label="Phone" value={user.phone ?? "—"} />
          <Field label="Email" value={user.email ?? "—"} />
          <Field label="Account type" value={user.role} />
        </div>
      </section>

      <KycPanel verified={user.kyc_status === "verified"} onVerified={onVerified} />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-navy-900">{value}</div>
    </div>
  );
}

function KycPanel({ verified, onVerified }: { verified: boolean; onVerified: (u: User) => void }) {
  const [aadhaar, setAadhaar] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.ekyc(aadhaar, otp);
      onVerified(res.data);
    } catch (err) {
      setError(errorMessage(err, "Verification failed"));
    } finally {
      setLoading(false);
    }
  };

  if (verified) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-card">
        <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold text-navy-900">
          <ShieldCheck size={20} className="text-green-600" /> Identity Verification
        </h2>
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          <BadgeCheck size={18} /> Aadhaar verified — full access unlocked.
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-card">
      <h2 className="mb-2 flex items-center gap-2 font-display text-lg font-semibold text-navy-900">
        <ShieldCheck size={20} className="text-iris-500" /> Verify Your Identity (Aadhaar e-KYC)
      </h2>
      <p className="mb-4 text-xs text-slate-500">
        Complete Aadhaar e-KYC to book verified stays. Demo: any 12-digit number with OTP{" "}
        <code>123456</code>.
      </p>
      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          inputMode="numeric"
          maxLength={12}
          value={aadhaar}
          onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, ""))}
          placeholder="12-digit Aadhaar number"
          className="input"
          required
        />
        <input
          inputMode="numeric"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          placeholder="OTP"
          className="input"
          required
        />
        <div className="sm:col-span-2">
          <button disabled={loading} className="btn-primary">
            {loading ? "Verifying…" : "Verify Aadhaar"}
          </button>
        </div>
      </form>
    </section>
  );
}

/* ------------------------------- Settings ------------------------------- */

function SettingsTab({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-card">
        <h2 className="mb-4 font-display text-lg font-semibold text-navy-900">Preferences</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Language</label>
            <select className="input max-w-xs">
              <option>English</option>
              <option>हिन्दी (Hindi)</option>
            </select>
          </div>
          <Toggle label="Email notifications" defaultChecked />
          <Toggle label="Push notifications" />
        </div>
        <p className="mt-4 text-xs text-slate-400">
          Preferences are illustrative in this demo.
        </p>
      </section>

      <section className="rounded-xl border border-red-200 bg-white p-6 shadow-card">
        <h2 className="mb-1 font-display text-lg font-semibold text-navy-900">Account</h2>
        <p className="mb-4 text-sm text-slate-500">Sign out of your account on this device.</p>
        <button onClick={onLogout} className="btn-outline text-red-600 hover:bg-red-50">
          <LogOut size={16} /> Logout
        </button>
      </section>
    </div>
  );
}

function Toggle({ label, defaultChecked }: { label: string; defaultChecked?: boolean }) {
  const [on, setOn] = useState(!!defaultChecked);
  return (
    <label className="flex cursor-pointer items-center justify-between">
      <span className="text-sm text-navy-800">{label}</span>
      <button
        type="button"
        onClick={() => setOn((v) => !v)}
        className={`relative h-6 w-11 rounded-full transition ${on ? "bg-iris-500" : "bg-slate-300"}`}
        aria-pressed={on}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`}
        />
      </button>
    </label>
  );
}

/* ------------------------------ Shared bits ----------------------------- */

function EmptyState({
  icon: Icon,
  title,
  body,
  action,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center shadow-card">
      <span className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-cream text-iris-500">
        <Icon size={26} />
      </span>
      <p className="font-display text-lg font-semibold text-navy-900">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">{body}</p>
      {action}
    </div>
  );
}

function StatusBadge({ status }: { status: Booking["status"] }) {
  const map: Record<Booking["status"], string> = {
    confirmed: "bg-green-100 text-green-700",
    pending: "bg-amber-100 text-amber-700",
    used: "bg-slate-100 text-slate-600",
    cancelled: "bg-red-100 text-red-700",
    refunded: "bg-blue-100 text-blue-700",
  };
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${map[status]}`}>{status}</span>
  );
}

/* --------------------------- Floating AI widget ------------------------- */

function AIWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<{ role: "user" | "ai"; text: string }[]>([
    { role: "ai", text: "Namaste! Ask me about Hawa Mahal, City Palace or Jantar Mantar." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await aiApi.chat(q);
      setMessages((m) => [...m, { role: "ai", text: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "ai", text: errorMessage(err) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-5 z-50 flex h-[28rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lift">
          <div className="flex items-center justify-between bg-navy-900 px-4 py-3 text-white">
            <span className="flex items-center gap-2 font-display font-semibold">
              <Sparkles size={16} className="text-gold-400" /> AI Heritage Guide
            </span>
            <button onClick={() => setOpen(false)} aria-label="Close chat">
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto p-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  m.role === "user" ? "ml-auto bg-iris-500 text-white" : "bg-cream text-navy-900"
                }`}
              >
                {m.text}
              </div>
            ))}
            {loading && (
              <div className="w-fit rounded-lg bg-cream px-3 py-2 text-sm text-slate-400">Thinking…</div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="flex gap-2 border-t border-slate-100 p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              className="input"
            />
            <button className="btn-primary px-3" aria-label="Send">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-50 grid h-14 w-14 place-items-center rounded-full bg-iris-500 text-white shadow-iris transition hover:bg-iris-600"
        aria-label="Toggle AI guide"
      >
        {open ? <X size={22} /> : <Bot size={24} />}
      </button>
    </>
  );
}